"use client";

import Image from "next/image";
import { cn } from "@/lib/utils";
import * as images from "@/components/images/Images";
import { ServicesProcessWorkProps } from "./types";
import { memo } from "react";
import { IconIdType } from "@/components/ui/Icon";
import { ServicesProcessWorkMap } from "./constants";
import { Typography } from "@/components/ui/typography";
import { ProcessWorkCardMobile } from "@/components/screen/global-components/process";

const ServicesProcessWork = ({
  variant = "standard",
  className,
}: ServicesProcessWorkProps) => {
  const { title, description, translationSteps } =
    ServicesProcessWorkMap[variant];

  return (
    <section className={cn("section bg-white lg:bg-transparent", className)}>
      <div className="container mx-auto flex flex-col">
        <div className="flex flex-col relative py-12 lg:py-20">
          <div className="flex flex-col justify-center gap-2 md:gap-6 md:w-full md:max-w-[712px] md:mx-auto">
            <Typography variant="heading4" className="text-center">
              <h2>{title}</h2>
            </Typography>
            <div className="flex justify-center text-gray-600">
              <Typography
                variant="subHeadingSm"
                className="text-center"
                asChild
              >
                <p>{description}</p>
              </Typography>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-6 lg:hidden max-w-[567] px-[10.5px]">
            {translationSteps.map(
              ({ iconId, title, description }, idx, array) => {
                return (
                  <ProcessWorkCardMobile
                    key={idx}
                    index={idx + 1}
                    iconId={iconId as IconIdType}
                    title={title}
                    description={description}
                    isLast={idx === array.length - 1}
                  />
                );
              }
            )}
          </div>

          <div className="hidden lg:block">
            <div className="grid grid-cols-[1fr_1fr_1fr] items-start gap-4 pt-12">
              <div className="flex flex-col gap-8 relative overflow-hidden">
                <div className="flex items-start gap-2 3xl:w-[90%]">
                  <Image
                    alt="elit_icon"
                    src={translationSteps[0].image}
                    width={0}
                    height={0}
                    className="w-20 z-2"
                    priority
                  />
                  <div className="flex flex-col gap-4">
                    <span className="text-[20px] font-medium">
                      {translationSteps[0].title}
                    </span>
                    <p className="text-sm">{translationSteps[0].description}</p>
                  </div>
                </div>

                <div className="flex justify-end">
                  <div className="flex flex-col gap-2 justify-center items-center w-[60%]">
                    <Image
                      alt="built_in_icon"
                      src={translationSteps[1].image}
                      width={0}
                      height={0}
                      className="w-20"
                      priority
                    />
                    <div className="flex flex-col gap-4 justify-center items-center">
                      <span className="text-[20px] font-medium text-center">
                        {translationSteps[1].title}
                      </span>
                      <p className="text-center text-sm">
                        {translationSteps[1].description}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="absolute top-[8.5%] 3xl:top-[-32%] z-0 left-9 w-[52.5%] 3xl:w-[58%]">
                  <Image
                    alt="bg_standart_1"
                    width={0}
                    height={0}
                    src={images.bg_standart_1}
                    className="w-full h-full"
                  />
                </div>
              </div>

              <div className="flex justify-end items-center relative h-full w-full mx-auto">
                <div className="flex items-start gap-2 w-[80%] 3xl:w-[70%] absolute top-[110px] 3xl:top-[53px] z-4">
                  <Image
                    alt="experince_icon"
                    src={translationSteps[2].image}
                    width={0}
                    height={0}
                    className="w-20"
                    priority
                  />
                  <div className="flex flex-col gap-4">
                    <span className="text-[20px] font-medium">
                      {translationSteps[2].title}
                    </span>
                    <p className="text-sm">{translationSteps[2].description}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-end gap-16 relative overflow-hidden">
                <div className="flex items-start gap-2 w-[70%]">
                  <Image
                    alt="dedicated_icon"
                    src={translationSteps[3].image}
                    width={0}
                    height={0}
                    className="w-20 z-2 bg-white"
                    priority
                  />
                  <div className="flex flex-col gap-4">
                    <span className="text-[20px] font-medium">
                     {translationSteps[3].title}
                    </span>
                    <p className="text-sm">{translationSteps[3].description}</p>
                  </div>
                </div>

                <div className="flex items-start gap-2 w-[70%]">
                  <Image
                    alt="approval_icon"
                    src={translationSteps[4].image}
                    width={0}
                    height={0}
                    className="w-20"
                    priority
                  />
                  <div className="flex flex-col gap-4">
                    <span className="text-[20px] font-medium">
                      {translationSteps[4].title}
                    </span>
                    <p className="text-sm">{translationSteps[4].description}</p>
                  </div>
                </div>
                <div className="absolute top-[-17%] 3xl:top-[-48.5%] z-0 left-[38.5%] 3xl:left-[36%] w-3">
                  <Image
                    alt="bg_standart_4"
                    width={0}
                    height={0}
                    src={images.bg_standart_4}
                    className="w-full h-full"
                  />
                </div>
              </div>
            </div>
            <div className="absolute top-[62%] 3xl:top-[57%] z-1 left-[24.8%] 3xl:left-[24.4%] w-[19%] 3xl:w-[22%]">
              <Image
                alt="bg_standart_2"
                width={0}
                height={0}
                src={images.bg_standart_2}
                className="w-full h-full"
              />
            </div>
            <div className="absolute top-[35.5%] 3xl:top-[40%] 3xl:right-[20%] z-2 right-[21.5%] w-[35%] 3xl:w-[37%] h-[35%] 3xl:h-[32%]">
              <Image
                alt="bg_standart_3"
                width={0}
                height={0}
                src={images.bg_standart_3}
                className="w-full h-full"
              />
              <div className="3xl:flex hidden bg-[#F5F7FA] h-20 w-[70%] absolute bottom-0 z-0"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default memo(ServicesProcessWork);
